if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.swiperInstance = null;
      this.mobileSwiperInstance = null;
      this.currentPin = null;
    }

    connectedCallback() {
      // WaitFor Swiper to load if not already
      if (typeof Swiper !== 'undefined') {
        this.init();
      } else {
        // Simple polling if script is deferred
        const checkSwiper = setInterval(() => {
          if (typeof Swiper !== 'undefined') {
            clearInterval(checkSwiper);
            this.init();
          }
        }, 100);
      }
    }

    init() {
      // Force initial visibility of all slides
      this.querySelectorAll('.swiper-slide').forEach(s => s.style.display = '');
      this.hideMobileDuplicates();
      this.initMainSwiper();
      this.bindEvents();
    }

    hideMobileDuplicates() {
      if (!window.matchMedia('(max-width: 1023px)').matches) return false;
      let changed = false;
      this.querySelectorAll('.swiper-slide[data-pin-duplicate]').forEach((slide) => {
        if (slide.style.display !== 'none') {
          slide.style.display = 'none';
          slide.classList.add('hidden', 'swiper-slide-hidden');
          changed = true;
        }
      });
      return changed;
    }

    initMainSwiper() {
      const swiperContainer = this.querySelector('.product-gallery-swiper');
      if (!swiperContainer) return;

      this.swiperInstance = new Swiper(swiperContainer, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: false,
        autoHeight: false,
        pagination: {
          el: '.swiper-pagination-product-gallery',
          clickable: true,
          type: 'bullets',
        },
        navigation: {
          nextEl: swiperContainer.querySelector('.gallery-nav-next'),
          prevEl: swiperContainer.querySelector('.gallery-nav-prev'),
        },
        scrollbar: {
          el: '.swiper-scrollbar-product-gallery',
          draggable: true,
          hide: false,
          dragClass: 'swiper-scrollbar-main-gallery',
        },
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
        watchSlidesProgress: true,
        on: {
          init: (swiper) => this.handleSlideChange(swiper),
          slideChange: (swiper) => this.handleSlideChange(swiper),
        }
      });
    }

    handleSlideChange(swiper) {
      let activeSlideIndex = swiper.activeIndex;
      let activeSlide = swiper.slides[activeSlideIndex];

      // If the current active slide is hidden, look for the next visible one
      while (activeSlide && activeSlide.style.display === 'none') {
        activeSlideIndex++;
        activeSlide = swiper.slides[activeSlideIndex];
      }

      const zoomBtn = this.querySelector('.gallery-zoom-trigger');

      if (activeSlide && zoomBtn) {
        const zoomUrl = activeSlide.dataset.zoomUrl;
        if (zoomUrl) {
          zoomBtn.dataset.zoomTarget = zoomUrl;
          zoomBtn.style.display = 'block';
        } else {
          zoomBtn.style.display = 'none';
        }
      }
    }

    bindEvents() {
      this.addEventListener('click', (e) => {
        const target = e.target;

        // 1. Zoom Trigger
        const zoomTrigger = target.closest('.gallery-zoom-trigger');
        if (zoomTrigger) {
          e.preventDefault();
          e.stopPropagation();
          const url = zoomTrigger.dataset.zoomTarget;
          this.openZoom(url);
          return;
        }

        // 2. Desktop Pin
        const pin = target.closest('.gallery-pin');
        if (pin) {
          e.preventDefault();
          e.stopPropagation();
          this.openDesktopModal(pin);
          return;
        }

        // 3. Mobile Details Button
        const detailsBtn = target.closest('[data-open-mobile-details]');
        if (detailsBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.openMobileModal();
          return;
        }
      });

      // Close Modals
      document.getElementById('ClosePinModal')?.addEventListener('click', () => this.closeDesktopModal());
      document.getElementById('CloseMobileModal')?.addEventListener('click', () => this.closeMobileModal());
      // Close Zoom
      document.getElementById('CloseZoomModal')?.addEventListener('click', () => this.closeZoom());

      // Close Mobile Overlay
      const mobileModal = document.getElementById('PinModalMobile');
      if (mobileModal) {
        mobileModal.addEventListener('click', (e) => {
          if (e.target === mobileModal) this.closeMobileModal();
        });
      }
      // Close desktop Overlay
      const desktopModal = document.getElementById('PinModalDesktop');
      if (desktopModal) {
        desktopModal.addEventListener('click', (e) => {
          if (e.target === desktopModal) this.closeDesktopModal();
        });
      }

      // Desktop Navigation Arrows
      const nextBtn = document.getElementById('PinDesktopNext');
      const prevBtn = document.getElementById('PinDesktopPrev');

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handlePinNavigation('next');
        });
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handlePinNavigation('prev');
        });
      }
    }

    // --- LOGIC: Variant Filtering ---
    setActiveMedia(mediaId, variant) {
      if (!this.swiperInstance) return;

      // Find slide index with this mediaId
      const slide = this.querySelector(`[data-media-id="${mediaId}"]`);
      if (slide) {
        const index = Array.from(slide.parentElement.children).indexOf(slide);
        this.swiperInstance.slideTo(index);
      }

      if (variant && variant.option1) {
        this.filterByColor(variant.option1); // Assuming standard option1 is color, or use existing logic
      }
    }

    filterByColor(colorValue) {
      if (!this.swiperInstance) return;

      const allSlides = this.querySelectorAll('.swiper-slide');
      let visibleCount = 0;
      let changeMap = new Map(); // Store intended state

      // Normalize target
      const targetColor = colorValue ? colorValue.toLowerCase().trim() : null;

      // Pass 1: Determine visibility
      allSlides.forEach((slide) => {
        // If no target color is provided, show everything
        if (!targetColor) {
          changeMap.set(slide, true);
          visibleCount++;
          return;
        }

        const slideColorRaw = slide.getAttribute('data-color');
        const slideColor = slideColorRaw ? slideColorRaw.toLowerCase().trim() : '';

        // Show if Generic (no color) OR Match
        // We treat empty string as generic
        const isGeneric = !slideColor || slideColor === '';
        const isMatch = slideColor === targetColor;

        if (isGeneric || isMatch) {
          changeMap.set(slide, true);
          visibleCount++;
        } else {
          changeMap.set(slide, false);
        }
      });

      // SAFETY FALLBACK: If filtering hides ALL slides, revert to showing ALL.
      if (visibleCount === 0 && allSlides.length > 0) {
        console.warn('MediaGallery: Filter matched 0 slides. Reverting to Show All.');
        allSlides.forEach(s => changeMap.set(s, true));
        visibleCount = allSlides.length;
      }

      // Pass 2: Apply changes
      let hasDOMChanges = false;
      allSlides.forEach(slide => {
        const shouldShow = changeMap.get(slide);
        if (shouldShow) {
          if (slide.style.display === 'none') {
            slide.style.display = '';
            slide.classList.remove('hidden', 'swiper-slide-hidden');
            hasDOMChanges = true;
          }
        } else {
          if (slide.style.display !== 'none') {
            slide.style.display = 'none';
            slide.classList.add('hidden', 'swiper-slide-hidden');
            hasDOMChanges = true;
          }
        }
      });

      if (this.hideMobileDuplicates()) hasDOMChanges = true;

      if (hasDOMChanges || visibleCount > 0) {
        this.swiperInstance.update();
      }
    }

    // --- MODAL: DESKTOP ---
    openDesktopModal(pin) {
      if (!pin) return;

      this.currentPin = pin;
      let modal = document.getElementById('PinModalDesktop');
      let contentContainer = document.getElementById('PinModalContent');
      let pinContentContainer = document.getElementById('PinModalContentContainer');
      let blockId = pin.dataset.pinBlockId;

      let template = document.getElementById(`PinTemplate-${blockId}`);

      if (modal && contentContainer && pinContentContainer && template) {
        contentContainer.innerHTML = template.innerHTML;
        modal.classList.remove('translate-x-full', 'opacity-0');
        pinContentContainer.classList.remove('translate-x-full');
        document.body.classList.add('overflow-hidden');
      }
    }

    handlePinNavigation(direction) {
      if (!this.currentPin) return;

      const allPins = Array.from(document.querySelectorAll('.gallery-pin'));
      if (allPins.length <= 1) return;

      const currentIndex = allPins.indexOf(this.currentPin);
      if (currentIndex === -1) return;

      let nextIndex;
      if (direction === 'next') {
        nextIndex = currentIndex + 1;
        if (nextIndex >= allPins.length) nextIndex = 0;
      } else {
        nextIndex = currentIndex - 1;
        if (nextIndex < 0) nextIndex = allPins.length - 1;
      }

      const nextPin = allPins[nextIndex];
      if (nextPin) {
        this.openDesktopModal(nextPin);
      }
    }

    closeDesktopModal() {
      let modal = document.getElementById('PinModalDesktop');
      let content = document.getElementById('PinModalContentContainer');
      if (content) content.classList.add('translate-x-full');
      document.body.classList.remove('overflow-hidden');
      if (modal) {
        modal.classList.add('translate-x-full');
        setTimeout(() => {
          modal.classList.add('opacity-0');
        }, 300);
      }
    }

    // --- MODAL: MOBILE ---
    openMobileModal() {
      let modal = document.getElementById('PinModalMobile');
      let content = document.getElementById('MobileSheetContent');
      if (!modal) return;

      modal.classList.remove('pointer-events-none', 'opacity-0');
      if (content) content.classList.remove('translate-y-full');
      document.body.classList.add('overflow-hidden');

      if (!this.mobileSwiperInstance) {
        this.mobileSwiperInstance = new Swiper('.mobile-pin-swiper', {
          slidesPerView: 1,
          spaceBetween: 16,
          navigation: {
            nextEl: '.swiper-button-next-pin-mobile',
            prevEl: '.swiper-button-prev-pin-mobile',
          },
        });
      }
    }

    closeMobileModal() {
      let modal = document.getElementById('PinModalMobile');
      let content = document.getElementById('MobileSheetContent');

      if (content) content.classList.add('translate-y-full');
      document.body.classList.remove('overflow-hidden');
      if (modal) {
        // Delay fading out overlay to match transition
        setTimeout(() => {
          modal.classList.add('pointer-events-none', 'opacity-0');
        }, 300);
      }
    }

    // --- ZOOM ---
    openZoom(url) {
      let modal = document.getElementById('ZoomModal');
      let img = document.getElementById('ZoomImage');

      if (modal && img) {
        img.src = url;
        modal.classList.remove('hidden');
      }
    }

    closeZoom() {
      let modal = document.getElementById('ZoomModal');
      if (modal) modal.classList.add('hidden');
    }
  });
}
